import { Activity, Database, ExternalLink, Menu, ShieldCheck, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Ledger } from './components/Ledger';
import { PitchLab } from './components/PitchLab';
import { TransferShelf } from './components/TransferShelf';
import { nextFixture, rosterSource } from './data/seed';
import { createPrediction, getOwnerKey, loadPredictions, scorePrediction } from './lib/api';
import type { Prediction } from './types';

function App() {
  const [ownerKey, setOwnerKey] = useState('');
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [mode, setMode] = useState<'cloud' | 'local'>('local');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const owner = getOwnerKey();
    setOwnerKey(owner);
    void loadPredictions(owner).then(result => { setPredictions(result.items); setMode(result.mode); });
  }, []);

  const save = async (prediction: Prediction) => {
    const result = await createPrediction(prediction);
    setMode(result.mode);
    setPredictions(current => [result.item, ...current.filter(item => item.id !== result.item.id)]);
  };

  const score = async (id: string, actual: Partial<Prediction>) => {
    const result = await scorePrediction(id, ownerKey, actual);
    setMode(result.mode);
    setPredictions(current => current.map(item => item.id === id ? result.item : item));
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="Manchester Signal Room home"><span className="brand-mark">M</span><span><strong>Manchester</strong><small>Signal Room</small></span></a>
        <nav className={menuOpen ? 'open' : ''}>
          <a href="#match-lab" onClick={() => setMenuOpen(false)}>Match Lab</a><a href="#transfers" onClick={() => setMenuOpen(false)}>Transfer Shelf</a><a href="#ledger" onClick={() => setMenuOpen(false)}>Ledger</a>
        </nav>
        <div className="top-status"><Database size={15}/><span>{mode === 'cloud' ? 'D1 connected' : 'Local fallback'}</span></div>
        <button className="menu-button" onClick={() => setMenuOpen(value => !value)} aria-label="Toggle navigation">{menuOpen ? <X/> : <Menu/>}</button>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy">
            <p className="hero-label"><Activity size={16}/> Evidence, selection and accountability</p>
            <h1>Read the market.<br/>Call the XI.<br/><em>Keep score.</em></h1>
            <p>A Manchester United intelligence workspace that separates credible transfer signals from noise and turns every match prediction into a scored historical record.</p>
            <div className="hero-actions"><a className="primary-button" href="#match-lab">Predict next match</a><a className="text-link" href="#transfers">Open transfer shelf <ExternalLink size={16}/></a></div>
          </div>
          <div className="hero-fixture">
            <div className="fixture-top"><span>Next modelled match</span><ShieldCheck size={18}/></div>
            <div className="fixture-clubs"><div className="club-monogram united">MU</div><b>v</b><div className="club-monogram opponent">ATM</div></div>
            <h2>Manchester United<br/><span>{nextFixture.opponent}</span></h2>
            <div className="fixture-details"><span>01 AUG 2026</span><span>{nextFixture.venue}</span><span>{nextFixture.competition}</span></div>
            <a href={nextFixture.sourceUrl} target="_blank" rel="noreferrer">Competition source <ExternalLink size={14}/></a>
          </div>
          <div className="hero-stripe" aria-hidden="true"/>
        </section>

        {ownerKey && <PitchLab ownerKey={ownerKey} onSave={save} saveMode={mode}/>}        
        <TransferShelf />
        <Ledger predictions={predictions} onScore={score}/>

        <section className="source-footer">
          <div><strong>Roster source</strong><span>Manchester United official men’s team page · checked 26 Jul 2026</span></div>
          <a href={rosterSource.url} target="_blank" rel="noreferrer">Open official roster <ExternalLink size={15}/></a>
        </section>
      </main>
      <footer><span>Manchester Signal Room · independent supporter-built prototype</span><span>Transfer scores are evidence indices, not betting advice or club information.</span></footer>
    </div>
  );
}

export default App;
